import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import type {
  DisbursementRow,
  DisbursementRowRef,
  DisbursementRowStatus,
  SendClientDisbursementPayload,
} from '@/models/disbursementTypes';
import {
  isRowCheckable,
  isRowActionable,
  selectAllKeys,
  reconcileSelection,
  bulkSubmitRefs,
  bulkUndoRefs,
} from '../utils/disbursementSelectionRules';
import { rowKeyForRef, CLIENT_ROW_KEY } from '@/utils/disbursementHelpers';

/** A view row: the read-path row overlaid with live in-flight / transient-error state. */
export interface DisbursementRowView extends DisbursementRow {
  state: DisbursementRowStatus | 'in-progress';
}

interface UseDisbursementsOptions {
  /**
   * Asks the user to confirm a reversal. The component owns the actual dialog
   * (a UI concern); the composable owns the flow. Resolves true to proceed.
   */
  confirmUndo: (recipientLabel: string) => Promise<boolean>;
  /** Same contract for recording the client's disbursement as paid outside Payabli. */
  confirmRecordManual: (recipientLabel: string) => Promise<boolean>;
  /** Same contract for canceling the client's payment link. */
  confirmCancel: (recipientLabel: string, amount: number) => Promise<boolean>;
}

/**
 * Orchestrates the Step 6 disbursement table: reads server state from the
 * settlement store, overlays live per-row busy/error state into view rows, owns
 * checkbox selection (with the invoice ↔ client-net dependency, via
 * disbursementSelectionRules), and exposes the submit/undo flows the template
 * binds to. Keeps the component a thin template.
 *
 * Server state and the one-call-per-op submit/undo live in the store (the
 * settlement domain's single source of truth); selection and view derivation
 * live here so the table stays declarative.
 */
export function useDisbursements(options: UseDisbursementsOptions) {
  const store = useSettlementStore();
  const { disbursements } = storeToRefs(store);

  const loading = computed(() => disbursements.value.loading);
  const loadError = computed(() => disbursements.value.error);
  const data = computed(() => disbursements.value.data);
  const anySubmitted = computed(() => !!data.value?.anySubmitted);
  const negativeNet = computed(() => !!data.value?.clientNetNegative);

  // sc-46626. The client row is lifted out of `rows` by normalizeDisbursements when the flag is on,
  // so it renders in its own section with no checkbox and never reaches the selection rules.
  const payabliEnabled = computed(() => !!data.value?.payabliEnabled);
  const payabliConnected = computed(() => !!data.value?.payabliConnected);
  const trustBalance = computed(() => data.value?.trustBalance ?? null);
  const clientRow = computed<DisbursementRowView | null>(() => {
    const row = data.value?.clientRow;
    if (!row) return null;
    const busy = disbursements.value.submittingKeys.includes(row.key);
    const transientError = disbursements.value.rowErrors[row.key];
    if (busy) return { ...row, state: 'in-progress' };
    if (transientError) {
      return { ...row, state: row.status === 'done' ? 'done' : 'error', message: transientError };
    }
    return { ...row, state: row.status };
  });

  /** View rows: base read-path status, overlaid with in-flight / transient-error state. */
  const rows = computed<DisbursementRowView[]>(() =>
    (data.value?.rows ?? []).map((row) => {
      const busy = disbursements.value.submittingKeys.includes(row.key);
      const transientError = disbursements.value.rowErrors[row.key] || null;
      if (busy) return { ...row, state: 'in-progress' };
      if (transientError) {
        // A failed *undo* leaves the row submitted (still 'done'); a failed *submit*
        // leaves it pending. Keep a submitted row as "Submitted" and just surface
        // the reason inline — don't flip it to a Retry/error state.
        const state = row.status === 'done' ? 'done' : 'error';
        return { ...row, state, message: transientError };
      }
      return { ...row, state: row.status };
    }),
  );

  /**
   * This client row's own operation is in flight. Distinct from anyBusy: the Send modal must
   * not spin (and must stay dismissable) because some unrelated bulk row is submitting.
   */
  const clientSending = computed(() =>
    disbursements.value.submittingKeys.includes(CLIENT_ROW_KEY),
  );

  /** The last failure recorded against the client row, for the compose modal to show in place. */
  const clientError = computed<string | null>(
    () => disbursements.value.rowErrors[CLIENT_ROW_KEY] ?? null,
  );

  /** A multi-row submit/undo is in flight (drives the bulk button's spinner). */
  const bulkBusy = computed(() => disbursements.value.bulkBusy);

  // The backend serializes submits per-settlement (concurrent calls 409), so the
  // UI keeps a single operation in flight — every action disables while one runs.
  const anyBusy = computed(
    () =>
      disbursements.value.bulkBusy ||
      disbursements.value.submittingKeys.length > 0,
  );

  // ── Selection (with the invoice ↔ client-net dependency, see rules module) ──
  const selectedKeys = ref<string[]>([]);
  const isSelected = (key: string): boolean => selectedKeys.value.includes(key);
  const isCheckable = (row: DisbursementRow): boolean =>
    isRowCheckable(row, rows.value, new Set(selectedKeys.value));

  const actionableKeys = computed(() => selectAllKeys(rows.value));
  /** Nothing is selectable right now — "select all" is a no-op and should be disabled. */
  const hasActionableRows = computed(() => actionableKeys.value.length > 0);
  const allSelected = computed(
    () =>
      actionableKeys.value.length > 0 &&
      actionableKeys.value.every((k) => selectedKeys.value.includes(k)),
  );
  const someSelected = computed(
    () => selectedKeys.value.length > 0 && !allSelected.value,
  );

  function toggleRow(key: string, value: boolean): void {
    const next = value
      ? [...selectedKeys.value, key]
      : selectedKeys.value.filter((k) => k !== key);
    // Reconcile so a (de)selection cascades per the dependency rules.
    selectedKeys.value = reconcileSelection(rows.value, next);
  }

  function toggleAll(value: boolean): void {
    selectedKeys.value = value ? actionableKeys.value : [];
  }

  const clearSelection = (): void => {
    selectedKeys.value = [];
  };

  // After a data refresh, drop keys that no longer exist / are no longer valid.
  watch(rows, () => {
    selectedKeys.value = reconcileSelection(rows.value, selectedKeys.value);
  });

  const submitRefs = computed(() => bulkSubmitRefs(rows.value, selectedKeys.value));
  const undoRefs = computed(() => bulkUndoRefs(rows.value, selectedKeys.value));
  const canBulkSubmit = computed(
    () => submitRefs.value.length > 0 && !anyBusy.value,
  );
  const canBulkUndo = computed(
    () => undoRefs.value.length > 0 && !anyBusy.value,
  );

  // The store already surfaces failures (inline per-row errors + a snackbar) and
  // rethrows so programmatic callers can react; these UI flows have nothing more
  // to do with the rejection, so they swallow it to avoid an unhandled promise.
  async function runSubmit(refs: DisbursementRowRef[]): Promise<void> {
    if (!refs.length) return;
    try {
      await store.submitDisbursements(refs);
    } catch {
      /* already surfaced by the store */
    }
  }

  async function runUndo(refs: DisbursementRowRef[]): Promise<void> {
    if (!refs.length) return;
    try {
      await store.undoDisbursements(refs);
    } catch {
      /* already surfaced by the store */
    }
  }

  const undoLabel = (rows: DisbursementRow[]): string =>
    rows.length === 1 ? rows[0].recipientName : `${rows.length} disbursements`;

  const submitRow = (row: DisbursementRowView): Promise<void> =>
    runSubmit([row.ref]);

  async function undoRow(row: DisbursementRowView): Promise<void> {
    if (!(await options.confirmUndo(row.recipientName))) return;
    await runUndo([row.ref]);
  }

  async function submitSelected(): Promise<void> {
    const refs = submitRefs.value;
    if (!refs.length) return;
    await runSubmit(refs);
    clearSelection();
  }

  async function undoSelected(): Promise<void> {
    const refs = undoRefs.value;
    if (!refs.length) return;
    // Confirm with the recipients being reversed; the backend enforces ordering
    // (client net before firm invoice) and returns per-row results.
    const keys = new Set(refs.map(rowKeyForRef));
    const undoingRows = rows.value.filter((r) => keys.has(r.key));
    if (!(await options.confirmUndo(undoLabel(undoingRows)))) return;
    await runUndo(refs);
    clearSelection();
  }

  /**
   * Resolves true only when a request actually went out. Rejects on failure rather than
   * swallowing: the caller has to keep its compose modal open, which it can't decide if every
   * outcome looks the same. The store has already surfaced the reason.
   */
  const sendClientDisbursement = (
    payload: SendClientDisbursementPayload,
  ): Promise<boolean> => store.sendClientDisbursement(payload);

  /** Same contract as sendClientDisbursement: true only when an email actually queued. */
  const resendClientDisbursementEmail = (
    payload: SendClientDisbursementPayload,
  ): Promise<boolean> => store.resendClientDisbursementEmail(payload);

  const loadClientDisbursementEmail = () => store.loadClientDisbursementEmail();

  async function recordManualDisbursement(): Promise<void> {
    const row = clientRow.value;
    if (!row) return;
    if (!(await options.confirmRecordManual(row.recipientName))) return;
    await store.recordManualClientDisbursement().catch(() => {
      // Already surfaced inline and via snackbar by the store; swallowing keeps a refused
      // confirmation and a failed write looking the same to the template.
    });
  }

  async function cancelClientDisbursement(): Promise<void> {
    const row = clientRow.value;
    if (!row?.payabli) return;
    if (!(await options.confirmCancel(row.recipientName, row.payabli.amount))) return;
    await store.cancelClientDisbursement().catch(() => {
      // Already surfaced inline and via snackbar by the store.
    });
  }

  function reload(): void {
    store.loadDisbursements();
  }

  onMounted(reload);
  onUnmounted(() => store.cancelDisbursementsLoad());

  return {
    // server-derived state
    rows,
    loading,
    loadError,
    anySubmitted,
    negativeNet,
    anyBusy,
    bulkBusy,
    // payabli (sc-46626)
    payabliEnabled,
    payabliConnected,
    clientSending,
    clientError,
    trustBalance,
    clientRow,
    // selection
    isSelected,
    isCheckable,
    hasActionableRows,
    allSelected,
    someSelected,
    toggleRow,
    toggleAll,
    canBulkSubmit,
    canBulkUndo,
    // actions
    submitRow,
    undoRow,
    submitSelected,
    undoSelected,
    recordManualDisbursement,
    sendClientDisbursement,
    resendClientDisbursementEmail,
    loadClientDisbursementEmail,
    cancelClientDisbursement,
    reload,
  };
}
